import { graphql } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import get from 'lodash.get';
import PropTypes from 'prop-types';
import React, { useRef } from 'react';

import { ArrowLink } from '../components/Common/Links';
import RichTextArticle from '../components/Common/RichText/RichTextArticle';
import Layout, { BACKGROUND } from '../components/Layouts/Layout';
import LearningCenterSeo from '../components/LearningCenter/LearningCenterSeo';
import ContentContainer from '../components/PageSections/ContentContainer';
import PageSection from '../components/PageSections/PageSection';
import FooterNav from '../components/Resources/FooterNav';
import * as styles from './resource.module.scss';

const Resource = ({ data, pageContext }) => {
  const returnToTopRef = useRef(null);
  const content = data.post;
  const ArticleContent = data.post.body;
  const hideHeroSection = content.hideHeroSection;
  const isSvg = get(content, 'heroImage.file.contentType') === 'image/svg+xml';
  const mainImage = get(content, 'heroImage.file.url');

  return (
    <Layout background={BACKGROUND.WHITE} isDach={pageContext.isDach}>
      <div ref={returnToTopRef} className={'publish-date'} data-post-date={content.publishDate} />
      <ContentContainer>
        {/*<BreadCrumbNav
          linkText="Back to Resources"
          linkUrl="/resources/"
          shareUrl={url}
          shareTitle={get(content, 'title')}
          hideSocialIcons={content.hideSocialIcons}
        />*/}
        {!hideHeroSection && (
          <div className={styles.hero} data-analytics-section={'resource-header'}>
            <div
              className={styles.contentContainer}
              data-analytics-component={'header-copy'}
              data-analytics-id={content.title}
            >
              <div className={styles.contentType}>{content.contentType?.title}</div>
              <h1>{content.title}</h1>
              {get(content, 'downloadableContent.file.url') && (
                <ArrowLink
                  url={get(content, 'downloadableContent.file.url')}
                  label={'Download the paper'}
                  isArrowDown
                  isLarge
                  isExternal
                  isDownload
                />
              )}
            </div>
            <>
              {isSvg ? (
                <img
                  data-analytics-component={'header-image'}
                  loading={'eager'}
                  fetchpriority={'high'}
                  src={mainImage}
                  alt={'Hero'}
                />
              ) : (
                <GatsbyImage
                  data-analytics-component={'header-image'}
                  loading={'eager'}
                  fetchpriority={'high'}
                  image={get(content, 'heroImage.gatsbyImageData')}
                  alt={get(content, 'title', '')}
                />
              )}
            </>
          </div>
        )}
      </ContentContainer>
      <PageSection isTopSection={content.hideHeroSection} className={hideHeroSection ? styles.pullUp : ''}>
        <RichTextArticle copy={ArticleContent} />
      </PageSection>
      <FooterNav backLink={'/resources/'} backLabel={'Return to Resources'} returnRef={returnToTopRef} />
    </Layout>
  );
};

export const Head = ({ data: { post }, location }) => {
  const mainImage = get(post, 'heroImage.file.url');
  const { ogImage, ...rest } = get(post, 'pageMetaData', {}) || {};
  const meta = {
    ogImageUrl: get(ogImage, 'resize.src') ? ogImage.resize.src : mainImage,
    ...rest,
  };
  return (
    <>
      <LearningCenterSeo meta={meta} slug={location.pathname} />
      <meta name="zd-site-verification" content={process.env.GATSBY_ZENDESK_CRAWLER_KEY} />
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'resources',
              item: 'https://www.fullstory.com/resources/',
            },
            {
              '@type': 'ListItem',
              position: 3,
              name: post.title,
            },
          ],
        })}
      </script>
    </>
  );
};

export const pageQuery = graphql`
  query ResourceQuery($pageId: String!) {
    post: contentfulPageResource(contentful_id: { eq: $pageId }) {
      title
      type {
        typeName
      }
      publishDate
      hideSocialIcons
      hideHeroSection
      theme {
        themeName
      }
      pageMetaData {
        ...PageMetaData
      }
      articleDescription {
        childMarkdownRemark {
          html
        }
      }
      ctaExternalLink
      downloadableContent {
        file {
          url
        }
      }
      heroImage {
        file {
          url
          contentType
        }
        gatsbyImageData(formats: [AUTO, WEBP], layout: FULL_WIDTH, placeholder: NONE)
      }
      body {
        raw
        references {
          ... on ContentfulAsset {
            ...AssetEmbed
          }
          ... on ContentfulEmbedVideo {
            ...VideoEmbed
          }
          ... on ContentfulEmbedInlineImage {
            ...InlinePostImage
          }
          ... on Node {
            ... on ContentfulSectionCallout {
              ...SectionCallout
            }
          }
          ... on Node {
            ... on ContentfulEmbedJumpLink {
              ...JumpLink
            }
          }
          ... on Node {
            ... on ContentfulComponentCallToAction {
              ...CallToAction
            }
          }
          ... on Node {
            ... on ContentfulEmbedLink {
              ...LinkEmbed
            }
          }
        }
      }
      contentType {
        viewLabel
        title
        slug
      }
    }
  }
`;

Resource.propTypes = {
  data: PropTypes.object,
  location: PropTypes.any,
};

export default Resource;
